import { useEffect, useState } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogOpenChangeData,
    DialogSurface,
    DialogTitle,
    Radio,
    RadioGroup,
    Spinner,
    Text,
    makeStyles,
    tokens,
} from '@fluentui/react-components';
import { MailRegular } from '@fluentui/react-icons';
import { Contact } from '../../../../data/types';
import { Column, Row } from '../../../common';

const useStyles = makeStyles({
    contactList: {
        maxHeight: '320px',
        overflowY: 'auto',
    },
    contactOption: {
        paddingBlock: tokens.spacingVerticalS,
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    },
});

export type EmailInvoiceDialogProps = {
    open: boolean;
    contacts: Contact[];
    invoiceNumber?: string;
    sending: boolean;
    onOpenChange: (open: boolean) => void;
    onSend: (contact: Contact) => Promise<void>;
};

export const EmailInvoiceDialog = (props: EmailInvoiceDialogProps) => {
    const { open, contacts, invoiceNumber, sending, onOpenChange, onSend } = props;
    const [selectedContactId, setSelectedContactId] = useState('');
    const styles = useStyles();

    const selectedContact = contacts.find((contact) => contact.contactId === selectedContactId);
    const canSend = !!selectedContact?.contactId && !!selectedContact.email?.trim() && !sending;

    const handleOpenChange = (_event: unknown, data: DialogOpenChangeData) => {
        if (!sending) {
            onOpenChange(data.open);
        }
    };

    const handleSend = async () => {
        if (canSend && selectedContact) {
            await onSend(selectedContact);
        }
    };

    useEffect(() => {
        if (open) {
            setSelectedContactId(contacts[0]?.contactId || (contacts.length ? 'missing-contact-id-0' : ''));
        }
    }, [contacts, open]);

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogSurface aria-describedby={undefined}>
                <DialogBody>
                    <DialogTitle>
                        <Row gap='medium'>
                            <Column width='auto' vAlign='middle'>
                                <MailRegular />
                            </Column>
                            <Column vAlign='middle'>Email Invoice {invoiceNumber}</Column>
                        </Row>
                    </DialogTitle>
                    <DialogContent>
                        <Column gap='medium'>
                            <Text>Select one client contact to receive the invoice PDF.</Text>
                            {contacts.length ? (
                                <RadioGroup
                                    className={styles.contactList}
                                    value={selectedContactId}
                                    onChange={(_event, data) => setSelectedContactId(data.value)}
                                    aria-label='Invoice recipient'
                                >
                                    {contacts.map((contact, index) => {
                                        const contactId = contact.contactId || `missing-contact-id-${index}`;
                                        const unavailable = !contact.contactId || !contact.email?.trim();

                                        return (
                                            <Radio
                                                className={styles.contactOption}
                                                key={contactId}
                                                value={contactId}
                                                disabled={unavailable}
                                                label={
                                                    <Column gap='none'>
                                                        <Text weight='semibold'>{contact.name || 'Unnamed contact'}</Text>
                                                        <Text size={200}>
                                                            {contact.email?.trim() || 'Email address required'}
                                                        </Text>
                                                    </Column>
                                                }
                                            />
                                        );
                                    })}
                                </RadioGroup>
                            ) : (
                                <Text>This client does not have any contacts.</Text>
                            )}
                        </Column>
                    </DialogContent>
                    <DialogActions>
                        <Button appearance='primary' disabled={!canSend} onClick={() => void handleSend()}>
                            {sending ? <Spinner size='tiny' label='Emailing invoice' /> : 'Email Invoice'}
                        </Button>
                        <Button appearance='secondary' disabled={sending} onClick={() => onOpenChange(false)}>
                            Cancel
                        </Button>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};
